import type { User, GroupMember } from '../types';

const API_BASE = '/api';

// ── Global API loading handler ─────────────────────────────────────────────
let _apiRequestCount = 0;
let _apiLoadingHandler: ((loading: boolean, message?: string) => void) | null = null;

export function registerApiLoadingHandler(fn: (loading: boolean, message?: string) => void): void {
  _apiLoadingHandler = fn;
}

// ── Global 401 unauthorized handler ───────────────────────────────────────
// Dispatches a custom event so the app can gracefully log out without a
// full page reload (which would cause a loop when multiple requests fail).
const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';
let _unauthorizedFired = false;

function dispatchUnauthorized(): void {
  if (_unauthorizedFired) return;
  _unauthorizedFired = true;
  localStorage.removeItem('ledger_token');
  localStorage.removeItem('ledger_user');
  localStorage.removeItem('ledger_groupId');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
  }
}

/** Call this after a successful login to re-arm the 401 handler. */
export function resetUnauthorizedFlag(): void {
  _unauthorizedFired = false;
}

export type AuthSessionResponse = { token: string; user: User };

export type AuthModeResponse = { domainFingerprint: string; isLocalhostMode: boolean };
export type RegistrationStatusResponse = { allowRegistration: boolean };

export type WebAuthnCredentialDto = {
  id: string;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string | null;
};

export type PasswordlessRequestResponse = { success: boolean; fallbackLink?: string };

export type NotificationDto = {
  id: string;
  userId: string;
  title: string;
  content: string;
  type: string;
  isRead: number | boolean;
  createdAt: string;
};

export type NotificationListResponse = {
  items: NotificationDto[];
  total: number;
  unreadCount: number;
  hasMore: boolean;
  limit: number;
  offset: number;
};

/**
 * 功能：發送帶認證標頭的 fetch 並解析 JSON 回應
 * @template T 預期回應 JSON 形別
 * @param {string} path 相對於 /api 的路徑
 * @param {RequestInit} [options={}] fetch 選項
 * @returns {Promise<T | null>} JSON 或 204 時為 null
 */
async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T | null> {
  _apiRequestCount++;
  if (_apiRequestCount === 1) {
    _apiLoadingHandler?.(true, '請稍候...');
  }
  try {
    const token = localStorage.getItem('ledger_token');
    const user = localStorage.getItem('ledger_user');
    const userData = user ? JSON.parse(user) : null;

    const isFormData = options.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(userData ? { 'x-user-id': userData.id } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

    if (response.status === 401) {
      dispatchUnauthorized();
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'API Request Failed');
    }

    return response.status !== 204 ? ((await response.json()) as T) : null;
  } finally {
    _apiRequestCount--;
    if (_apiRequestCount === 0) {
      _apiLoadingHandler?.(false);
    }
  }
}

/**
 * 功能：集中封裝家庭帳本 REST API 呼叫
 * @returns {object} 依領域分組的 API 方法
 */
export function createLedgerApi() {
  return {
    auth: {
      mode: () => request<AuthModeResponse>('/auth/mode'),
      registrationStatus: () => request<RegistrationStatusResponse>('/auth/registration-status'),
      login: (data: { email: string; password: string }) =>
        request<AuthSessionResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
      register: (data: { email: string; nickname: string }) =>
        request<{ success: boolean; fallbackLink?: string }>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
      requestPasswordless: (data: { email: string }) =>
        request<PasswordlessRequestResponse>('/auth/passwordless/request', { method: 'POST', body: JSON.stringify(data) }),
      consumeMagicLink: (data: { token: string; email: string }) =>
        request<AuthSessionResponse>('/auth/passwordless/consume', { method: 'POST', body: JSON.stringify(data) }),
      webauthnRegisterOptions: (data: { email: string }) =>
        request<PublicKeyCredentialCreationOptionsJSON>('/auth/webauthn/register/options', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      webauthnRegisterVerify: (data: unknown) =>
        request<AuthSessionResponse>('/auth/webauthn/register/verify', { method: 'POST', body: JSON.stringify(data) }),
      webauthnLoginOptions: (data: { email: string }) =>
        request<PublicKeyCredentialRequestOptionsJSON>('/auth/webauthn/login/options', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      webauthnLoginVerify: (data: unknown) =>
        request<AuthSessionResponse>('/auth/webauthn/login/verify', { method: 'POST', body: JSON.stringify(data) }),
      webauthnCredentialsList: () => request<WebAuthnCredentialDto[]>('/auth/webauthn/credentials'),
      webauthnCredentialsDelete: (id: string) =>
        request<unknown>(`/auth/webauthn/credentials/${id}`, { method: 'DELETE' }),
    },
    expenses: {
      list: (groupId?: string) => request<unknown[]>(groupId ? `/expense?groupId=${encodeURIComponent(groupId)}` : '/expense'),
      create: (data: FormData) => request<unknown>('/expense', { method: 'POST', body: data }),
      update: (id: string, data: FormData) => request<unknown>(`/expense/${id}`, { method: 'PUT', body: data }),
      delete: (id: string, groupId?: string) => request<unknown>(`/expense/${id}${groupId ? `?groupId=${encodeURIComponent(groupId)}` : ''}`, { method: 'DELETE' }),
    },
    settle: {
      getSummary: (otherUserId: string) => request<unknown>(`/settlement/summary/${otherUserId}`),
      settle: (data: FormData | Record<string, unknown>) =>
        request<unknown>('/settlement/settle', {
          method: 'POST',
          body: data instanceof FormData ? data : JSON.stringify(data),
        }),
      history: (groupId?: string) => request<unknown[]>(groupId ? `/settlement/history?groupId=${encodeURIComponent(groupId)}` : '/settlement/history'),
    },
    accounts: {
      list: () => request<unknown[]>('/account'),
      create: (data: unknown) => request<unknown>('/account', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: unknown) => request<unknown>(`/account/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request<unknown>(`/account/${id}`, { method: 'DELETE' }),
    },
    users: {
      list: () => request<unknown[]>('/members'),
      search: (email: string) => request<User>(`/users/search?email=${encodeURIComponent(email)}`),
      invite: (userId: string) => request<unknown>('/members/invite', { method: 'POST', body: JSON.stringify({ userId }) }),
      updateProfile: (data: FormData) => request<User>('/users/profile', { method: 'POST', body: data }),
      update: (data: { nickname: string }) =>
        request<{ user: User }>('/users/profile', { method: 'PUT', body: JSON.stringify(data) }),
    },
    notifications: {
      /**
       * 功能：分頁取得通知列表
       * @param {{ limit?: number; offset?: number }} [params] 分頁參數
       * @returns {Promise<NotificationListResponse | null>}
       */
      list: (params?: { limit?: number; offset?: number }) => {
        const limit = params?.limit ?? 10;
        const offset = params?.offset ?? 0;
        return request<NotificationListResponse>(
          `/notifications?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`,
        );
      },
      remind: (data: { debtorId: string; amount: number; expenseTitle: string }) =>
        request<unknown>('/notifications/remind', { method: 'POST', body: JSON.stringify(data) }),
      markRead: (id: string) => request<unknown>('/notifications/read', { method: 'POST', body: JSON.stringify({ id }) }),
      markAllRead: () => request<unknown>('/notifications/read-all', { method: 'POST' }),
    },
    setup: {
      status: () => request<{ isInitialized: boolean }>('/setup/status'),
      initialize: (data: { setupKey: string }) =>
        request<AuthSessionResponse>('/setup/initialize', { method: 'POST', body: JSON.stringify(data) }),
    },
    groups: {
      list: () => request<{ groups: unknown[] }>('/groups'),
      create: (data: { name: string; description: string | null }) =>
        request<{ group: unknown }>('/groups', { method: 'POST', body: JSON.stringify(data) }),
      get: (id: string) => request<unknown>(`/groups/${id}`),
      update: (id: string, data: { name?: string; description?: string | null }) =>
        request<unknown>(`/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request<unknown>(`/groups/${id}`, { method: 'DELETE' }),
      listMembers: (groupId: string) =>
        request<{ members: GroupMember[] }>(`/groups/${groupId}/members`),
      updateMemberRole: (groupId: string, userId: string, role: 'admin' | 'member') =>
        request<{ success: boolean }>(`/groups/${groupId}/members/${userId}/role`, {
          method: 'PUT',
          body: JSON.stringify({ role }),
        }),
      removeMember: (groupId: string, userId: string) =>
        request<{ success: boolean }>(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),
      listAll: () => request<{ groups: unknown[] }>('/admin/groups'),
    },
    admin: {
      getSettings: () => request<{ allowRegistration: boolean }>('/admin/settings'),
      updateSettings: (data: { allowRegistration: boolean }) =>
        request<{ success: boolean; allowRegistration: boolean }>('/admin/settings', {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      listGroupMembers: (groupId: string) =>
        request<{ group: unknown; members: GroupMember[] }>(`/admin/groups/${groupId}/members`),
      updateGroup: (groupId: string, data: { name: string; description?: string | null }) =>
        request<{ success: boolean }>(`/admin/groups/${groupId}`, { method: 'PUT', body: JSON.stringify(data) }),
      removeMember: (groupId: string, userId: string) =>
        request<{ success: boolean }>(`/admin/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),
      updateMemberRole: (groupId: string, userId: string, role: 'admin' | 'member') =>
        request<{ success: boolean }>(`/admin/groups/${groupId}/members/${userId}/role`, {
          method: 'PUT',
          body: JSON.stringify({ role }),
        }),
    },
    invites: {
      send: (data: { email: string }) =>
        request<unknown>('/members/invite', { method: 'POST', body: JSON.stringify(data) }),
      list: () => request<unknown[]>('/invites'),
      revoke: (id: string) => request<unknown>(`/invites/${id}/revoke`, { method: 'POST' }),
    },
  };
}

/** @type {ReturnType<typeof createLedgerApi>} 單例 API 客戶端 */
export const api = createLedgerApi();
